import type { Edge } from "@xyflow/react";
import type { FullAlignmentNode } from "@/hooks/use-full-alignment";
import type { ObjectiveNodeType, ObjectiveNodeData } from "./objective-node";
import type { StrategicNodeFlowType, StrategicNodeData, StrategicNodeType } from "./strategic-node";
import type { KrNodeFlowType, KrNodeData } from "./kr-node";

const OBJECTIVE_WIDTH = 224;  // w-56 = 14rem = 224px
const OBJECTIVE_HEIGHT = 140;
const STRATEGIC_WIDTH = 280;  // w-70 = 17.5rem = 280px
const STRATEGIC_HEIGHT = 100;
const KR_WIDTH = 200;         // w-50 = 12.5rem = 200px
const KR_HEIGHT = 100;
const HORIZONTAL_GAP = 40;
const VERTICAL_GAP = 80;

type FlowNode = ObjectiveNodeType | StrategicNodeFlowType | KrNodeFlowType;

const STRATEGIC_NODE_TYPES = new Set(["college_strategic_pillar", "college_goal", "focus_area", "tactic"]);

function getNodeDimensions(nodeType: string): { width: number; height: number } {
  if (nodeType === "key_result") {
    return { width: KR_WIDTH, height: KR_HEIGHT };
  }
  if (STRATEGIC_NODE_TYPES.has(nodeType)) {
    return { width: STRATEGIC_WIDTH, height: STRATEGIC_HEIGHT };
  }
  return { width: OBJECTIVE_WIDTH, height: OBJECTIVE_HEIGHT };
}

interface LayoutResult {
  nodes: FlowNode[];
  edges: Edge[];
}

/**
 * Converts the full alignment tree into React Flow nodes and edges.
 * Handles polymorphic nodes (strategic + objective) and DAG deduplication.
 */
/**
 * Recursively finds a node by ID in the alignment tree (DFS).
 */
export function findNode(
  roots: FullAlignmentNode[],
  id: string,
): FullAlignmentNode | null {
  for (const root of roots) {
    if (root.id === id) return root;
    const found = findNode(root.children, id);
    if (found) return found;
  }
  return null;
}

export function buildFlowLayout(
  roots: FullAlignmentNode[],
  expandedIds: Set<string>,
  onToggleExpand: (id: string) => void,
  onNavigate: (id: string) => void,
  onFocusNode: (id: string) => void,
  onShowDetail?: (id: string) => void,
  year?: number,
): LayoutResult {
  const nodes: FlowNode[] = [];
  const edges: Edge[] = [];
  const seenIds = new Map<string, number>();

  function getUniqueId(id: string): string {
    const count = seenIds.get(id) ?? 0;
    seenIds.set(id, count + 1);
    return count === 0 ? id : `${id}__dup${count}`;
  }

  function getSubtreeWidth(node: FullAlignmentNode): number {
    const { width } = getNodeDimensions(node.node_type);
    if (!expandedIds.has(node.id) || node.children.length === 0) {
      return width;
    }
    const childrenWidth = node.children.reduce(
      (sum: number, child: FullAlignmentNode) => sum + getSubtreeWidth(child),
      0,
    );
    const gaps = (node.children.length - 1) * HORIZONTAL_GAP;
    return Math.max(width, childrenWidth + gaps);
  }

  function layoutNode(
    node: FullAlignmentNode,
    x: number,
    y: number,
    parentFlowId: string | null,
  ): void {
    const { width, height } = getNodeDimensions(node.node_type);
    const subtreeWidth = getSubtreeWidth(node);
    const nodeX = x + (subtreeWidth - width) / 2;
    const flowId = getUniqueId(node.id);

    if (node.node_type === "key_result") {
      const data: KrNodeData = {
        krId: node.id,
        title: node.title,
        status: node.status,
        completionPercent: node.completion_percent,
        quarter: node.quarter,
        dueDate: node.due_date,
        krType: node.kr_type,
        targetValue: node.target_value,
        currentValue: node.current_value,
        year: year ?? null,
        onShowDetail: onShowDetail ?? null,
      };
      nodes.push({
        id: flowId,
        type: "kr",
        position: { x: nodeX, y },
        data,
      } as KrNodeFlowType);
    } else if (node.node_type === "objective") {
      const data: ObjectiveNodeData = {
        objectiveId: node.id,
        title: node.title,
        ownerName: node.owner_name,
        level: node.level,
        status: node.status,
        completionPercent: node.completion_percent,
        hasChildren: node.children.length > 0,
        isExpanded: expandedIds.has(node.id),
        onToggleExpand,
        onNavigate,
        onFocusNode,
        onShowDetail: onShowDetail ?? null,
      };
      nodes.push({
        id: flowId,
        type: "objective",
        position: { x: nodeX, y },
        data,
      } as ObjectiveNodeType);
    } else {
      const data: StrategicNodeData = {
        nodeId: node.id,
        nodeType: node.node_type as StrategicNodeType,
        title: node.title,
        horizon: node.horizon,
        referenceCode: node.reference_code,
        hasChildren: node.children.length > 0,
        isExpanded: expandedIds.has(node.id),
        onToggleExpand,
        onFocusNode,
        onShowDetail: onShowDetail ?? null,
      };
      nodes.push({
        id: flowId,
        type: "strategic",
        position: { x: nodeX, y },
        data,
      } as StrategicNodeFlowType);
    }

    if (parentFlowId) {
      edges.push({
        id: `${parentFlowId}-${flowId}`,
        source: parentFlowId,
        target: flowId,
        type: "smoothstep",
        style: { stroke: "hsl(200, 18%, 73%)", strokeWidth: 2 },
      });
    }

    if (expandedIds.has(node.id) && node.children.length > 0) {
      const childY = y + height + VERTICAL_GAP;
      let childX = x;

      for (const child of node.children) {
        const childWidth = getSubtreeWidth(child);
        layoutNode(child, childX, childY, flowId);
        childX += childWidth + HORIZONTAL_GAP;
      }
    }
  }

  let rootX = 0;
  for (const root of roots) {
    const rootWidth = getSubtreeWidth(root);
    layoutNode(root, rootX, 0, null);
    rootX += rootWidth + HORIZONTAL_GAP;
  }

  return { nodes, edges };
}

/**
 * Auto-expand top 3 levels (pillars, goals, focus areas).
 * Only collects nodes that have children.
 */
export function collectExpandableIds(roots: FullAlignmentNode[]): Set<string> {
  const ids = new Set<string>();

  function collect(nodes: FullAlignmentNode[], depth: number): void {
    if (depth >= 3) return;
    for (const node of nodes) {
      if (node.children.length > 0) {
        ids.add(node.id);
        collect(node.children, depth + 1);
      }
    }
  }

  collect(roots, 0);
  return ids;
}
